import React, {
  memo, useCallback, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Gear } from '@picsio/icons';
import { Icon, IconButton } from '@picsio/ui';
import Tooltip from '../Tooltip';
import localization from '../../shared/strings';
import { COLOR_LABELS, getContrastTextColor } from '../../helpers/colorsHelper';
import Logger from '../../services/Logger';
import { changeColor } from '../../store/actions/assets';
import { selectCanManageColorLabels, selectColorOptions } from '../../store/selectors/colorLabels';
import { navigate } from '../../helpers/history';
import makeTooltipText from './helpers/tooltipColorText';

const l10n = localization.CATALOG_ITEM.colorLabels;

function Colors({
  color, assetId, disabled, colorAt, colorBy,
}) {
  const dispatch = useDispatch();
  const colors = useSelector(selectColorOptions);
  const canManageColorLabels = useSelector(selectCanManageColorLabels);
  const userId = useSelector((state) => state.user._id);
  const [showDrop, setShowDrop] = useState(false);
  const selectedColor = useMemo(
    () => colors.find((colorItem) => colorItem.value === color)
      || colors.find((colorItem) => colorItem.value === COLOR_LABELS.noColor),
    [color, colors],
  );

  const handleClick = useCallback(
    () => {
      const loggerText = showDrop ? 'Close' : 'Open';
      Logger.log('User', `ThumbnailColors${loggerText}`);
      if (!disabled) setShowDrop(!showDrop);
    },
    [showDrop, disabled],
  );

  const handleChange = useCallback(
    (event) => {
      const value = event.currentTarget.dataset.color || COLOR_LABELS.noColor;
      if (!disabled) {
        if (value === COLOR_LABELS.noColor) {
          Logger.log('User', 'MainThumbnailColorsNoneClicked', {
            previousLabelId: color,
            userId,
          });
        } else {
          const label = colors.find((colorItem) => colorItem.value === value);
          Logger.log('User', 'MainThumbnailColorChanged', {
            labelId: value,
            isCustomNamed: label?.name !== value,
            userId,
          });
        }
        dispatch(changeColor([assetId], value));
      }
      setShowDrop(false);
    },
    [assetId, color, colors, disabled, dispatch, userId],
  );

  const handleManageClick = useCallback(() => {
    Logger.log('User', 'MainThumbnailColorsEditLabelsClicked', { hasManagePermission: true });
    navigate('/teammates?tab=color-labels');
  }, []);

  const tooltipText = useMemo(
    () => makeTooltipText(selectedColor.title || color, colorAt, colorBy),
    [color, selectedColor.title, colorAt, colorBy],
  );

  const { text, ...buttonStyles } = useMemo(
    () => ({
      text: selectedColor.value === COLOR_LABELS.noColor ? 'color' : selectedColor.name,
      background: selectedColor.background || 'none',
      color: getContrastTextColor(selectedColor.background),
    }),
    [selectedColor.background, selectedColor.name, selectedColor.value],
  );

  return (
    <div className="catalogItem__colors">
      <Tooltip content={tooltipText} placement="left">
        <span
          className={cn('catalogItem__colors-opener', {
            noColor: selectedColor.value === COLOR_LABELS.noColor,
          })}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyPress={handleClick}
          style={buttonStyles}
        >
          <span className="catalogItem__colors-text">
            {text}
          </span>
        </span>
      </Tooltip>
      <If condition={showDrop}>
        <div className="catalogItem__colors-list">
          {colors.map((colorItem) => (
            <Tooltip content={colorItem.title} placement="left" key={colorItem.value}>
              <div
                className={cn('catalogItem__colors-color', { act: colorItem.value === color })}
                tabIndex={0}
                role="button"
                aria-label={colorItem.title}
                style={{ background: colorItem.background }}
                data-color={colorItem.value}
                onClick={handleChange}
                onKeyPress={handleChange}
              >
                <If condition={colorItem.value === COLOR_LABELS.noColor}>none</If>
              </div>
            </Tooltip>
          ))}
          <If condition={canManageColorLabels}>
            <Tooltip content={l10n.manageTooltip} placement="left">
              <IconButton
                className="catalogItem__colors-settings"
                size="md"
                componentProps={{ 'aria-label': l10n.manageTooltip }}
                onClick={handleManageClick}
              >
                <Icon size="md"><Gear /></Icon>
              </IconButton>
            </Tooltip>
          </If>
        </div>
      </If>
    </div>
  );
}

Colors.propTypes = {
  assetId: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  colorAt: PropTypes.string,
  colorBy: PropTypes.shape({
    _id: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
  }),
};

export default memo(Colors);
